import {UserAction} from '@gongxigames/framework/integrations/pop/types';
import {CoreDI, LanguageService, lazyInject} from '@gongxigames/framework/core';
import {HTMLSystemPopup, IGlobalEventManger, PopupObject, TemplateUtils} from '@gongxigames/framework/slot';
import {createTimeSelector} from '@gongxigames/framework/integrations/pop/sessionTimer/timeSelector';

type SessionTimerParams = {
    onChoiceAction: (action: UserAction, interval?: number) => void;
    message: string;
    availablePeriods: number[];
};

type ButtonData = {
    text: string;
    onClick: () => void;
};

export class SessionTimer {
    @lazyInject(CoreDI.LanguageService)
    protected readonly languageService!: LanguageService;

    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected readonly eventManager!: IGlobalEventManger;

    protected _params: SessionTimerParams;
    protected _container!: HTMLDivElement;
    protected _popup!: HTMLDivElement;
    protected _content!: HTMLDivElement;
    protected TEMPLATE_NAME = 'popup';
    protected _popupObject!: PopupObject;
    protected _id = 'session-timer';

    protected _interval = -1;
    protected _defaultPeriodIndex = 0;

    public priority = 99999;

    public static showPopup(params: SessionTimerParams): void {
        const sessionTimer = new SessionTimer(params);

        sessionTimer.emitAddPopup();
    }

    public closedCallback: Function = () => {};

    constructor(params: SessionTimerParams) {
        this._params = params;

        this.initContainer();
        this.initContent();
        this.initButtons();
        this.initPopupObject();

        this._interval = this._params.availablePeriods[this._defaultPeriodIndex];
    }

    protected initContainer(): void {
        this._container = TemplateUtils.createHTMLDivElementByTemplate(this.TEMPLATE_NAME, {
            id: this._id,
            header: this.languageService.get('sessionTimerTitle')
        });

        this._popup = this.getHTMLDivElementFromContainer(`#${this._id}`);
        this._popup.classList.add('with-borders');
        this._params.availablePeriods.length === 1 && this._popup.classList.add('two-buttons');
    }

    protected initContent(): void {
        this._content = this.getHTMLDivElementFromContainer(`#${this._id}-content`);
        this._content.innerHTML = `${this.getMessageTextElement()}`;

        if (this._params.availablePeriods.length > 1) {
            this.addTimeSelector();
        }
    }

    protected initPopupObject(): void {
        const closeButton = this.getHTMLDivElementFromContainer(`#${this._id}-close`);

        closeButton.hidden = true;

        this._popupObject = {
            type: 2,
            popup: this._popup,
            parent: this._container,
            header: this.getHTMLDivElementFromContainer(`#${this._id}-title`),
            closeButton,
            content: this._content,
            priority: this.priority
        };
    }

    protected getMessageTextElement(): string {
        return `<p>${this._params.message}</p><p>${this.languageService.get('sessionTimerBottomText')}</p>`;
    }

    protected addTimeSelector(): void {
        const timeSelector = createTimeSelector(this._params.availablePeriods, this._defaultPeriodIndex,
            this.setInterval.bind(this));

        this._content.appendChild(timeSelector.view);
        this._content.classList.add('with-time-selector');
    }

    protected setInterval(time: number): void {
        this._interval = time;
    }

    protected initButtons(): void {
        this._popup.classList.add('buttons');

        const buttons: ButtonData[] = [
            {text: 'continue', onClick: this.onClickContinue.bind(this)},
            {text: 'quit', onClick: this.onClickQuit.bind(this)}
        ];

        buttons.forEach(btn => this.addButton(btn));
    }

    protected addButton(data: ButtonData): void {
        const button = document.createElement('button');
        const {text, onClick} = data;

        button.textContent = this.languageService.get(text);
        button.onclick = () => onClick();

        this._content.insertAdjacentElement('beforeend', button);
    }

    public show(): void {
        if (!this._container.parentElement) {
            document.body.insertAdjacentElement('beforeend', this._container);
        }

        this._container.hidden = false;
    }

    public close(): void {
        this._container.hidden = true;
        this._container.parentNode!.removeChild(this._container);
    }

    protected getHTMLDivElementFromContainer(selector: string): HTMLDivElement {
        return this._container.querySelector(`${selector}`) as HTMLDivElement;
    }

    public get popup(): PopupObject {
        return this._popupObject;
    }

    public resize(resizeParam: number): void {
    }

    protected onClickContinue(): void {
        this._params.onChoiceAction(UserAction.RESET_TIMER, this._interval);
        this.emitRemovePopup();
    }

    protected onClickQuit(): void {
        this._params.onChoiceAction(UserAction.QUIT);
        this.emitRemovePopup();
    }

    public emitAddPopup(): void {
        this.eventManager.emit('General.Popups.Add', this.systemPopup);
    }

    public emitRemovePopup(): void {
        this.eventManager.emit('General.Popups.Remove', this.systemPopup);
    }

    protected get systemPopup(): HTMLSystemPopup {
        return {
            id: 'session-timer-popup',
            priority: this.popup.priority,
            view: this.popup,
            type: 'HTML'
        };
    }
}
